import type { ContributorTask } from "@/features/contributor-tasks/contributor-tasks";
import type { IconName } from "@/lib/icons";
import type { ContributorHeaderCopy } from "./contributor-header-copy";

/** A header entry: a direct link, or a short menu of related tasks. */
export interface ContributorTab {
  key: string;
  label: string;
  icon: IconName;
  /** Link target; for a menu, the first item's page. */
  href: string;
  /** Other pages of the same entry (contracts for earnings...). */
  alsoOn?: string[];
  /** Present for a menu entry: the tasks it opens. */
  items?: ContributorTask[];
  /** Short value shown after the label, e.g. the available earnings. */
  badge?: string;
}

const byKey = (tasks: ContributorTask[], ...keys: string[]) =>
  keys.map((key) => tasks.find((task) => task.key === key)).filter((task): task is ContributorTask => Boolean(task));

/**
 * The signed-in header: one short, plain verb per kind of work. The two
 * validation tasks share a menu, the less frequent ones sit under "More".
 */
export function contributorTabs(
  lang: string,
  copy: ContributorHeaderCopy,
  tasks: ContributorTask[],
  isCollector: boolean,
  earningsAmount: string | null = null,
): ContributorTab[] {
  const base = `/${lang}`;
  const link = (key: string, label: string, icon: IconName): ContributorTab | null => {
    const task = tasks.find((t) => t.key === key);
    return task ? { key, label, icon, href: task.href } : null;
  };
  const menu = (key: string, label: string, icon: IconName, items: ContributorTask[]): ContributorTab | null =>
    items.length ? { key, label, icon, href: items[0].href, alsoOn: items.slice(1).map((i) => i.href), items } : null;

  return [
    link("speak", copy.speak, "mic"),
    link("transcribe", copy.write, "edit"),
    link("translate", copy.translate, "languages"),
    menu("validate", copy.verify, "checkCircle", byKey(tasks, "verifyAudio", "verifyText")),
    menu("more", copy.more, "moreHorizontal", tasks.filter((task) => !task.primary)),
    isCollector ? { key: "collection", label: copy.collection, icon: "users" as const, href: `${base}/collector` } : null,
    // One entry for the earnings, with the available amount in it (it used to
    // be a second button to the same page).
    {
      key: "earnings",
      label: copy.earnings,
      icon: "wallet" as const,
      href: `${base}/get-paid`,
      alsoOn: [`${base}/user/contracts`],
      ...(earningsAmount ? { badge: earningsAmount } : {}),
    },
  ].filter((tab): tab is ContributorTab => tab !== null);
}

export function isTabActive(tab: ContributorTab, pathname: string): boolean {
  const current = pathname.replace(/\/+$/, "");
  return [tab.href, ...(tab.alsoOn ?? [])].some((path) => current === path || current.startsWith(`${path}/`));
}
